import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Activity,
  ArrowRight,
  Bot,
  CheckCircle2,
  Cloud,
  ExternalLink,
  FolderKanban,
  Loader2,
  Monitor,
  Play,
  Rocket,
  Settings2,
  Terminal,
  Wifi,
  WifiOff,
  XCircle,
  Clock3
} from 'lucide-react';

import {
  useNavigate
} from 'react-router-dom';

import {
  useRealtime
} from '../contexts/RealtimeContext';

import {
  useProjects
} from '../contexts/ProjectContext';

import {
  supabase
} from '../lib/supabase';



import {
  PCControlHomeCard
} from '../components/pc-control/PCControlHomeCard';

import {
  AnimatedNumber
} from '../components/common/AnimatedNumber';

import {
  AnimatePresence,
  motion
} from 'motion/react';

import {
  kaizenMicroTransition,
  kaizenStateTransition
} from '../lib/kaizenMotion';

type RecentJob = {
  id: string;
  project_id?: string | null;
  type?: string | null;
  job_type?: string | null;
  status?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
};


export const OverviewPage: React.FC = () => {

  const navigate =
    useNavigate();


  const {
    device,
    deviceStatus,
    services,
    latencyMs,
    wifiSsid,
    sendJob
  } =
    useRealtime();


  const {
    projects,
    activeProject,
    isLoading: isProjectsLoading,
    error: projectsError
  } =
    useProjects();


  const [
    recentJobs,
    setRecentJobs
  ] =
    useState<RecentJob[]>(
      []
    );


  const [
    activityLoading,
    setActivityLoading
  ] =
    useState(
      true
    );


  const [
    activityError,
    setActivityError
  ] =
    useState<string | null>(
      null
    );


  const [
    isBuilding,
    setIsBuilding
  ] =
    useState(
      false
    );


  const [
    actionError,
    setActionError
  ] =
    useState<string | null>(
      null
    );


  const online =
    deviceStatus ===
    'online';


  const loadRecentActivity =
    useCallback(
      async () => {

        try {

          const {
            data,
            error
          } =
            await supabase
              .from(
                'kr_jobs'
              )
              .select(
                '*'
              )
              .order(
                'created_at',
                {
                  ascending: false
                }
              )
              .limit(
                6
              );


          if (error) {
            throw error;
          }


          setRecentJobs(
            (data || []) as RecentJob[]
          );


          setActivityError(
            null
          );

        }
        catch (error) {

          setActivityError(
            error instanceof Error
              ? error.message
              : String(error)
          );

        }
        finally {

          setActivityLoading(
            false
          );
        }
      },
      []
    );


  useEffect(
    () => {

      void loadRecentActivity();


      const timer =
        window.setInterval(
          () => {
            void loadRecentActivity();
          },
          15000
        );


      return () => {
        window.clearInterval(
          timer
        );
      };
    },
    [
      loadRecentActivity
    ]
  );


  const handleQuickBuild =
    async () => {

      if (!activeProject) {

        setActionError(
          'No active project selected.'
        );

        return;
      }


      if (
        !device?.id ||
        !online
      ) {

        setActionError(
          'KAIZEN Agent is offline.'
        );

        return;
      }


      setIsBuilding(
        true
      );


      setActionError(
        null
      );


      try {

        await sendJob({
          deviceId:
            device.id,

          projectId:
            activeProject.id,

          type:
            'build',

          payload: {
            command:
              activeProject.buildCommand
          },

          workingDirectory:
            activeProject.localPath
        });


        window.setTimeout(
          () => {
            void loadRecentActivity();
          },
          1200
        );

      }
      catch (error) {

        setActionError(
          error instanceof Error
            ? `Build dispatch failed: ${error.message}`
            : 'Build dispatch failed.'
        );

      }
      finally {

        setIsBuilding(
          false
        );
      }
    };


  const serviceHealthy =
    (
      value: unknown
    ) => {

      const normalized =
        String(
          value || ''
        ).toLowerCase();


      return [
        'connected',
        'ready',
        'online',
        'healthy',
        'available'
      ].includes(
        normalized
      );
    };


  const serviceItems =
    useMemo(
      () => [
        {
          label: 'Agent',
          value: services.agent
        },
        {
          label: 'PowerShell',
          value: services.powershell
        },
        {
          label: 'Chrome',
          value: services.chromeExtension
        },
        {
          label: 'JARVIS',
          value: services.chatgptBridge
        }
      ],
      [
        services
      ]
    );


  const readyServices =
    serviceItems.filter(
      item =>
        serviceHealthy(
          item.value
        )
    ).length;


  const projectName =
    (
      projectId?: string | null
    ) => {

      if (!projectId) {
        return 'General';
      }


      return (
        projects.find(
          project =>
            project.id === projectId
        )?.name ||
        'Project'
      );
    };


  const jobLabel =
    (
      job: RecentJob
    ) => {

      const type =
        job.type ||
        job.job_type ||
        'job';


      const names:
        Record<string, string> =
        {
          build:
            'Build',

          deploy_production:
            'Production deployment',

          deploy_preview:
            'Preview deployment',

          powershell_script:
            'PowerShell script',

          powershell_command:
            'PowerShell command',

          assistant:
            'JARVIS task'
        };


      return (
        names[type] ||
        type
          .replace(
            /_/g,
            ' '
          )
          .replace(
            /\b\w/g,
            character =>
              character.toUpperCase()
          )
      );
    };


  const relativeTime =
    (
      value?: string | null
    ) => {

      if (!value) {
        return '';
      }


      const timestamp =
        new Date(
          value
        ).getTime();


      if (
        Number.isNaN(
          timestamp
        )
      ) {
        return '';
      }


      const seconds =
        Math.max(
          0,
          Math.floor(
            (
              Date.now() -
              timestamp
            ) /
            1000
          )
        );


      if (seconds < 60) {
        return 'now';
      }


      const minutes =
        Math.floor(
          seconds /
          60
        );


      if (minutes < 60) {
        return `${minutes}m`;
      }


      const hours =
        Math.floor(
          minutes /
          60
        );


      if (hours < 24) {
        return `${hours}h`;
      }


      return `${
        Math.floor(
          hours /
          24
        )
      }d`;
    };


  const quickActions = [
    {
      label:
        'Console',

      detail:
        'Run commands',

      icon:
        Terminal,

      onClick:
        () =>
          navigate(
            '/console'
          )
    },

    {
      label:
        'JARVIS',

      detail:
        'AI operator',

      icon:
        Bot,

      onClick:
        () =>
          navigate(
            '/assistant'
          )
    },

    {
      label:
        'Remote Desktop',

      detail:
        'Live workspace',

      icon:
        Monitor,

      onClick:
        () =>
          navigate(
            '/remote-desktop'
          )
    },

    {
      label:
        'Deploy',

      detail:
        'Cloudflare Pages',

      icon:
        Rocket,

      onClick:
        () =>
          navigate(
            '/deployments'
          )
    }
  ];


  return (

    <div
      className="
        space-y-5
        sm:space-y-6
        pb-4
      "
    >

      <section
        className="
          relative
          overflow-hidden
          rounded-[18px]
          border
          border-white/[0.09]
          bg-slate-950/60
          backdrop-blur-xl
          shadow-[0_22px_70px_rgba(0,0,0,0.24)]
        "
      >

        <div
          className="
            pointer-events-none
            absolute
            -top-16
            -left-14
            w-44
            h-44
            rounded-full
            bg-indigo-500/[0.08]
            blur-3xl
            motion-safe:animate-[pulse_5s_ease-in-out_infinite]
          "
        />


        <div
          className="
            pointer-events-none
            absolute
            -bottom-20
            right-0
            w-52
            h-52
            rounded-full
            bg-cyan-400/[0.045]
            blur-3xl
            motion-safe:animate-[pulse_7s_ease-in-out_infinite]
          "
        />


        <div
          className="
            pointer-events-none
            absolute
            inset-x-0
            top-0
            h-px
            bg-gradient-to-r
            from-transparent
            via-indigo-300/55
            to-transparent
            motion-safe:animate-pulse
          "
        />


        <div
          className="
            relative
            px-4
            sm:px-5
            pt-4
            pb-3
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
            "
          >

            <div
              className={`
                relative
                w-12
                h-12
                shrink-0
                rounded-xl
                border
                flex
                items-center
                justify-center
                overflow-visible

                ${
                  online
                    ? `
                        border-emerald-400/25
                        bg-emerald-400/[0.075]
                        text-emerald-300
                        shadow-[0_0_30px_rgba(52,211,153,0.08)]
                      `
                    : `
                        border-rose-400/25
                        bg-rose-400/[0.075]
                        text-rose-300
                      `
                }
              `}
            >

              {online && (

                <span
                  className="
                    absolute
                    inset-[7px]
                    rounded-lg
                    border
                    border-emerald-300/20
                    motion-safe:animate-ping
                    [animation-duration:2.8s]
                  "
                />

              )}


              {online
                ? (
                  <Wifi
                    className="
                      relative
                      z-10
                      w-[19px]
                      h-[19px]
                      motion-safe:animate-[pulse_3.2s_ease-in-out_infinite]
                    "
                  />
                )
                : (
                  <WifiOff
                    className="
                      relative
                      z-10
                      w-[19px]
                      h-[19px]
                    "
                  />
                )}

            </div>


            <div
              className="
                flex-1
                min-w-0
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-2
                  flex-wrap
                "
              >

                <h1
                  className="
                    text-[15px]
                    sm:text-base
                    font-semibold
                    tracking-tight
                    text-slate-100
                  "
                >
                  {online ? (wifiSsid || 'Network Online') : 'Network Offline'}
                </h1>


                <span
                  className={`
                    inline-flex
                    items-center
                    gap-1.5
                    rounded-md
                    border
                    px-2
                    py-1
                    text-[9px]
                    font-medium

                    ${
                      online
                        ? `
                            border-emerald-400/15
                            bg-emerald-400/[0.065]
                            text-emerald-300
                          `
                        : `
                            border-rose-400/15
                            bg-rose-400/[0.065]
                            text-rose-300
                          `
                    }
                  `}
                >

                  <span
                    className="
                      relative
                      flex
                      w-2
                      h-2
                    "
                  >

                    {online && (

                      <span
                        className="
                          absolute
                          inline-flex
                          h-full
                          w-full
                          rounded-full
                          bg-emerald-400
                          opacity-60
                          motion-safe:animate-ping
                        "
                      />

                    )}


                    <span
                      className={`
                        relative
                        inline-flex
                        w-2
                        h-2
                        rounded-full

                        ${
                          online
                            ? 'bg-emerald-400'
                            : 'bg-rose-400'
                        }
                      `}
                    />

                  </span>

                  {online
                    ? 'LIVE'
                    : 'OFFLINE'}

                </span>

              </div>


              <div
                className="
                  mt-1.5
                  flex
                  items-center
                  gap-3
                "
              >

                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    font-mono
                    text-[9px]
                    text-slate-500
                  "
                >

                  <span>
                    <AnimatedNumber
                      value={
                        typeof latencyMs === 'number'
                          ? latencyMs
                          : null
                      }
                      suffix="ms"
                    />
                  </span>


                  <div
                    className="
                      flex
                      items-end
                      gap-[2px]
                      h-3
                    "
                    aria-hidden="true"
                  >

                    <span
                      className="
                        block
                        w-[2px]
                        h-[4px]
                        rounded-full
                        bg-indigo-400/60
                        motion-safe:animate-pulse
                      "
                    />

                    <span
                      className="
                        block
                        w-[2px]
                        h-[8px]
                        rounded-full
                        bg-indigo-400/75
                        motion-safe:animate-pulse
                        [animation-delay:180ms]
                      "
                    />

                    <span
                      className="
                        block
                        w-[2px]
                        h-[11px]
                        rounded-full
                        bg-indigo-300
                        motion-safe:animate-pulse
                        [animation-delay:360ms]
                      "
                    />

                    <span
                      className="
                        block
                        w-[2px]
                        h-[7px]
                        rounded-full
                        bg-indigo-400/70
                        motion-safe:animate-pulse
                        [animation-delay:540ms]
                      "
                    />

                  </div>

                </div>


                <span
                  className="
                    text-slate-700
                    text-[9px]
                  "
                >
                  ·
                </span>


                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    text-[9px]
                    text-slate-500
                  "
                >

                  <Activity
                    className="
                      w-3
                      h-3
                      text-indigo-400
                      motion-safe:animate-pulse
                    "
                  />

                  <AnimatedNumber
                    value={readyServices}
                  />/4 systems ready

                </div>

              </div>

            </div>


            <button
              type="button"
              onClick={() =>
                navigate(
                  '/settings'
                )
              }
              className="
                w-9
                h-9
                shrink-0
                rounded-lg
                border
                border-white/[0.07]
                bg-white/[0.025]
                hover:bg-white/[0.055]
                text-slate-500
                hover:text-indigo-300
                flex
                items-center
                justify-center
                transition-all
                active:scale-95
              "
              title="System settings"
            >

              <Settings2
                className="
                  w-4
                  h-4
                "
              />

            </button>

          </div>


          <div
            className="
              mt-4
              grid
              grid-cols-4
              gap-1.5
            "
          >

            {serviceItems.map(
              (
                item,
                index
              ) => {

                const healthy =
                  serviceHealthy(
                    item.value
                  );


                return (

                  <div
                    key={
                      item.label
                    }
                    className="
                      relative
                      min-w-0
                      overflow-hidden
                      rounded-lg
                      border
                      border-white/[0.065]
                      bg-white/[0.022]
                      px-2
                      py-2.5
                      text-center
                    "
                  >

                    {healthy && (

                      <div
                        className="
                          pointer-events-none
                          absolute
                          inset-0
                          bg-emerald-400/[0.018]
                          motion-safe:animate-pulse
                        "
                        style={{
                          animationDelay:
                            `${index * 220}ms`
                        }}
                      />

                    )}


                    <div
                      className="
                        relative
                        flex
                        items-center
                        justify-center
                        gap-1.5
                      "
                    >

                      <span
                        className="
                          relative
                          flex
                          w-2
                          h-2
                          shrink-0
                        "
                      >

                        {healthy && (

                          <span
                            className="
                              absolute
                              inline-flex
                              h-full
                              w-full
                              rounded-full
                              bg-emerald-400
                              opacity-50
                              motion-safe:animate-ping
                            "
                            style={{
                              animationDelay:
                                `${index * 170}ms`
                            }}
                          />

                        )}


                        <span
                          className={`
                            relative
                            inline-flex
                            w-2
                            h-2
                            rounded-full

                            ${
                              healthy
                                ? `
                                    bg-emerald-400
                                    shadow-[0_0_10px_rgba(52,211,153,0.36)]
                                  `
                                : 'bg-slate-700'
                            }
                          `}
                        />

                      </span>


                      <span
                        className="
                          truncate
                          text-[8px]
                          sm:text-[9px]
                          font-medium
                          text-slate-500
                        "
                      >
                        {item.label}
                      </span>

                    </div>

                  </div>
                );
              }
            )}

          </div>


          <div
            className="
              mt-3
              h-px
              overflow-hidden
              rounded-full
              bg-white/[0.04]
            "
          >

            <div
              className="
                h-full
                w-1/2
                bg-gradient-to-r
                from-transparent
                via-indigo-400/50
                to-transparent
                motion-safe:animate-[pulse_2.6s_ease-in-out_infinite]
              "
            />

          </div>

        </div>

      </section>


      <section>

        <div
          className="
            px-0.5
            mb-2.5
            text-[9px]
            uppercase
            tracking-[0.14em]
            font-semibold
            text-slate-600
          "
        >
          Quick Actions
        </div>


        <div
          className="
            grid
            grid-cols-2
            lg:grid-cols-4
            gap-2
          "
        >

          {quickActions.map(
            action => {

              const Icon =
                action.icon;


              return (

                <button
                  key={
                    action.label
                  }
                  type="button"
                  onClick={
                    action.onClick
                  }
                  className="
                    min-h-[82px]
                    rounded-xl
                    border
                    border-white/[0.07]
                    bg-white/[0.025]
                    hover:bg-white/[0.05]
                    p-3
                    text-left
                    transition-all
                    active:scale-[0.985]
                  "
                >

                  <motion.div
                    className="
                      relative
                      w-8
                      h-8
                      rounded-lg
                      border
                      border-indigo-400/15
                      bg-indigo-400/[0.07]
                      text-indigo-300
                      flex
                      items-center
                      justify-center
                      shadow-[0_0_24px_rgba(99,102,241,0.05)]
                      motion-safe:animate-[pulse_5s_ease-in-out_infinite]
                    "
                    whileHover={{
                      scale: 1.08,
                      y: -1
                    }}
                    whileTap={{
                      scale: 0.96
                    }}
                    transition={
                      kaizenMicroTransition
                    }
                  >

                    <Icon className="w-4 h-4" />

                  </motion.div>


                  <div
                    className="
                      mt-2.5
                      text-[10px]
                      sm:text-[11px]
                      font-semibold
                      text-slate-200
                    "
                  >
                    {action.label}
                  </div>


                  <div
                    className="
                      mt-0.5
                      text-[9px]
                      text-slate-600
                    "
                  >
                    {action.detail}
                  </div>

                </button>
              );
            }
          )}

        </div>

      </section>



<PCControlHomeCard />

<section
        className="
          overflow-hidden
          rounded-[18px]
          border
          border-white/[0.08]
          bg-slate-950/48
          backdrop-blur-xl
        "
      >

        <div
          className="
            px-4
            sm:px-5
            py-3.5
            border-b
            border-white/[0.07]
            flex
            items-center
            justify-between
            gap-3
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              min-w-0
            "
          >

            <div
              className="
                w-9
                h-9
                rounded-xl
                shrink-0
                border
                border-indigo-400/20
                bg-indigo-400/10
                text-indigo-300
                flex
                items-center
                justify-center
              "
            >

              <FolderKanban className="w-[18px] h-[18px]" />

            </div>


            <div className="min-w-0">

              <span
                className="
                  block
                  text-[9px]
                  uppercase
                  tracking-[0.14em]
                  font-semibold
                  text-slate-600
                "
              >
                Active Project
              </span>


              <h2
                className="
                  mt-0.5
                  text-sm
                  font-semibold
                  text-slate-100
                  truncate
                "
              >
                {isProjectsLoading
                  ? 'Loading project…'
                  : (
                      activeProject?.name ||
                      'No Project Selected'
                    )}
              </h2>

            </div>

          </div>


          <button
            type="button"
            onClick={() =>
              navigate(
                '/projects'
              )
            }
            className="
              min-h-[34px]
              px-2.5
              rounded-lg
              border
              border-white/[0.07]
              bg-white/[0.025]
              hover:bg-white/[0.05]
              text-[9px]
              text-slate-400
              flex
              items-center
              gap-1.5
            "
          >

            Projects

            <ArrowRight className="w-3 h-3" />

          </button>

        </div>


        <div className="p-4 sm:p-5">

          {projectsError ? (

            <div
              className="
                text-[10px]
                text-rose-300
              "
            >
              {projectsError}
            </div>

          ) : activeProject ? (

            <>

              <p
                className="
                  text-[10px]
                  sm:text-[11px]
                  leading-relaxed
                  text-slate-500
                "
              >
                {activeProject.description ||
                  'Remote development workspace'}
              </p>


              <div
                className="
                  mt-3
                  flex
                  flex-wrap
                  gap-2
                "
              >

                <span
                  className="
                    inline-flex
                    items-center
                    gap-1.5
                    rounded-md
                    border
                    border-white/[0.07]
                    bg-white/[0.025]
                    px-2
                    py-1
                    text-[9px]
                    text-slate-400
                  "
                >

                  <Cloud className="w-3 h-3 text-indigo-400" />

                  {activeProject.cloudflareProjectName ||
                    'Cloudflare not configured'}

                </span>


                <span
                  className={`
                    inline-flex
                    items-center
                    gap-1.5
                    rounded-md
                    border
                    px-2
                    py-1
                    text-[9px]

                    ${
                      activeProject.productionUrl
                        ? `
                            border-emerald-400/15
                            bg-emerald-400/[0.06]
                            text-emerald-300
                          `
                        : `
                            border-amber-400/15
                            bg-amber-400/[0.06]
                            text-amber-300
                          `
                    }
                  `}
                >

                  <span
                    className={`
                      w-1.5
                      h-1.5
                      rounded-full

                      ${
                        activeProject.productionUrl
                          ? 'bg-emerald-400'
                          : 'bg-amber-400'
                      }
                    `}
                  />

                  {activeProject.productionUrl
                    ? 'Production configured'
                    : 'Site not configured'}

                </span>

              </div>


              <div
                className="
                  mt-4
                  grid
                  grid-cols-2
                  sm:grid-cols-4
                  gap-2
                "
              >

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/projects/${activeProject.id}`
                    )
                  }
                  className="
                    min-h-[40px]
                    rounded-lg
                    border
                    border-white/[0.07]
                    bg-white/[0.025]
                    hover:bg-white/[0.05]
                    text-[10px]
                    font-medium
                    text-slate-300
                  "
                >
                  Open
                </button>


                <button
                  type="button"
                  disabled={
                    isBuilding ||
                    !online
                  }
                  onClick={() =>
                    void handleQuickBuild()
                  }
                  className="
                    min-h-[40px]
                    rounded-lg
                    border
                    border-indigo-400/20
                    bg-indigo-500/90
                    hover:bg-indigo-500
                    text-[10px]
                    font-semibold
                    text-white
                    flex
                    items-center
                    justify-center
                    gap-1.5
                    disabled:opacity-45
                  "
                >

                  {isBuilding
                    ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    )
                    : (
                      <Play className="w-3.5 h-3.5" />
                    )}

                  Build

                </button>


                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      '/console'
                    )
                  }
                  className="
                    min-h-[40px]
                    rounded-lg
                    border
                    border-white/[0.07]
                    bg-white/[0.025]
                    hover:bg-white/[0.05]
                    text-[10px]
                    font-medium
                    text-slate-300
                  "
                >
                  Console
                </button>


                {activeProject.productionUrl ? (

                  <a
                    href={
                      activeProject.productionUrl
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="
                      min-h-[40px]
                      rounded-lg
                      border
                      border-indigo-400/15
                      bg-indigo-400/[0.06]
                      hover:bg-indigo-400/10
                      text-[10px]
                      font-medium
                      text-indigo-300
                      flex
                      items-center
                      justify-center
                      gap-1.5
                    "
                  >

                    Live Site

                    <ExternalLink className="w-3 h-3" />

                  </a>

                ) : (

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        '/projects'
                      )
                    }
                    className="
                      min-h-[40px]
                      rounded-lg
                      border
                      border-white/[0.07]
                      bg-white/[0.025]
                      text-[10px]
                      text-slate-500
                    "
                  >
                    Configure
                  </button>

                )}

              </div>


              {actionError && (

                <div
                  className="
                    mt-3
                    rounded-lg
                    border
                    border-rose-400/15
                    bg-rose-400/[0.06]
                    px-3
                    py-2.5
                    text-[10px]
                    text-rose-300
                  "
                >
                  {actionError}
                </div>

              )}

            </>

          ) : (

            <div
              className="
                py-5
                text-center
              "
            >

              <div
                className="
                  text-[10px]
                  text-slate-600
                "
              >
                No project selected.
              </div>


              <button
                type="button"
                onClick={() =>
                  navigate(
                    '/projects'
                  )
                }
                className="
                  mt-3
                  min-h-[38px]
                  px-4
                  rounded-lg
                  border
                  border-indigo-400/20
                  bg-indigo-500/10
                  text-[10px]
                  text-indigo-300
                "
              >
                Open Projects
              </button>

            </div>

          )}

        </div>

      </section>


      <section
        className="
          overflow-hidden
          rounded-[18px]
          border
          border-white/[0.08]
          bg-slate-950/48
          backdrop-blur-xl
        "
      >

        <div
          className="
            px-4
            sm:px-5
            py-3.5
            border-b
            border-white/[0.07]
            flex
            items-center
            justify-between
            gap-3
          "
        >

          <div
            className="
              flex
              items-center
              gap-2
            "
          >

            <Activity className="w-3.5 h-3.5 text-indigo-400" />

            <span
              className="
                text-[10px]
                font-semibold
                text-slate-300
              "
            >
              Recent Activity
            </span>

          </div>


          <button
            type="button"
            onClick={() =>
              navigate(
                '/history'
              )
            }
            className="
              min-h-[30px]
              px-2
              rounded-lg
              text-[9px]
              text-slate-500
              hover:text-indigo-300
              hover:bg-white/[0.04]
            "
          >
            View all
          </button>

        </div>


        <div>

          {activityLoading &&
          recentJobs.length === 0 ? (

            <div
              className="
                min-h-[120px]
                flex
                items-center
                justify-center
                text-slate-600
              "
            >

              <Loader2 className="w-4 h-4 animate-spin" />

            </div>

          ) : activityError &&
            recentJobs.length === 0 ? (

            <div
              className="
                px-4
                py-5
                text-[10px]
                text-rose-300
              "
            >
              Unable to load recent activity.
            </div>

          ) : recentJobs.length === 0 ? (

            <div
              className="
                px-4
                py-8
                text-center
                text-[10px]
                text-slate-600
              "
            >
              No recent activity yet.
            </div>

          ) : (

            <AnimatePresence
              initial={true}
              mode="popLayout"
            >
              {recentJobs.map(
                (
                  job,
                  index
                ) => {

                const status =
                  String(
                    job.status ||
                    'unknown'
                  );


                const succeeded =
                  status ===
                  'succeeded';


                const failed =
                  status ===
                  'failed';


                return (

                  <motion.div
                    key={
                      job.id
                    }
                    layout="position"
                    initial={{
                      opacity: 0,
                      y: -7
                    }}
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    exit={{
                      opacity: 0,
                      y: 5
                    }}
                    transition={{
                      ...kaizenStateTransition,
                      delay: Math.min(
                        index * 0.025,
                        0.12
                      )
                    }}
                    className={`
                      px-4
                      sm:px-5
                      py-3
                      flex
                      items-center
                      gap-3

                      ${
                        index !== recentJobs.length - 1
                          ? 'border-b border-white/[0.055]'
                          : ''
                      }
                    `}
                  >


                    <div
                      className={`
                        w-8
                        h-8
                        shrink-0
                        rounded-lg
                        border
                        flex
                        items-center
                        justify-center

                        ${
                          succeeded
                            ? `
                                border-emerald-400/15
                                bg-emerald-400/[0.06]
                                text-emerald-300
                              `
                            : failed
                              ? `
                                  border-rose-400/15
                                  bg-rose-400/[0.06]
                                  text-rose-300
                                `
                              : `
                                  border-amber-400/15
                                  bg-amber-400/[0.06]
                                  text-amber-300
                                `
                        }
                      `}
                    >

                      <AnimatePresence
                        mode="wait"
                        initial={false}
                      >
                        <motion.span
                          key={
                            status
                          }
                          initial={{
                            opacity: 0,
                            scale: 0.72
                          }}
                          animate={{
                            opacity: 1,
                            scale: 1
                          }}
                          exit={{
                            opacity: 0,
                            scale: 0.82
                          }}
                          transition={
                            kaizenMicroTransition
                          }
                          className="inline-flex"
                        >
                          {succeeded
                            ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )
                            : failed
                              ? (
                                <XCircle className="w-3.5 h-3.5" />
                              )
                              : (
                                <Clock3 className="w-3.5 h-3.5 motion-safe:animate-pulse" />
                              )}
                        </motion.span>
                      </AnimatePresence>

                    </div>


                    <div
                      className="
                        flex-1
                        min-w-0
                      "
                    >

                      <div
                        className="
                          text-[10px]
                          sm:text-[11px]
                          font-medium
                          text-slate-300
                          truncate
                        "
                      >
                        {jobLabel(
                          job
                        )}
                      </div>


                      <div
                        className="
                          mt-0.5
                          text-[9px]
                          text-slate-600
                          truncate
                        "
                      >
                        {projectName(
                          job.project_id
                        )}
                      </div>

                    </div>


                    <div
                      className="
                        shrink-0
                        text-right
                      "
                    >

                      <div
                        className={`
                          text-[9px]
                          font-medium
                          capitalize

                          ${
                            succeeded
                              ? 'text-emerald-300'
                              : failed
                                ? 'text-rose-300'
                                : 'text-amber-300'
                          }
                        `}
                      >
                        {status}
                      </div>


                      <div
                        className="
                          mt-0.5
                          font-mono
                          text-[8px]
                          text-slate-700
                        "
                      >
                        {relativeTime(
                          job.created_at
                        )}
                      </div>

                    </div>

                  </motion.div>
                );
                }
              )}
            </AnimatePresence>
          )}

        </div>

      </section>




    </div>
  );
};